// Mirrors the host Tenant Management contracts:
//   Volo.Abp.TenantManagement.*  (ABP ITenantAppService)
//   Finkit.TenantManagement.*    (IFinkitTenantAppService)
//   Finkit.Needs.Tenants.Dtos.*  (IFinkitTenantSetupService — Edit tabs)
// Only the fields the SPA consumes are typed. Numeric enum values are the wire
// contract — do not renumber.

import type { ServerTableRequest } from '@/components/tables/useServerDataTable';

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export interface TenantConnectionStringDto {
  name: string;
  value: string;
}

// Volo.Abp.TenantManagement.TenantDto (ExtensibleEntityDto<Guid>).
export interface TenantDto {
  id: string;
  name: string;
  concurrencyStamp: string;
  isSetupPending: boolean;
  connectionStrings?: TenantConnectionStringDto[] | null;
  extraProperties?: Record<string, unknown>;
}

// Finkit.TenantManagement.Dtos.FinkitTenantDto : TenantDto.
export interface FinkitTenantDto extends TenantDto {
  email?: string | null;
}

// Finkit.TenantManagement.TenantInputDto : PaginatedInputDto.
export type TenantListInput = ServerTableRequest & {
  name?: string;
};

// ---------------------------------------------------------------------------
// Create / Update (ABP ITenantAppService)
// ---------------------------------------------------------------------------

// Volo.Abp.TenantManagement.TenantConnectionStringCreateDto.
export interface TenantConnectionStringCreateDto {
  name: string;
  value: string;
}

// Volo.Abp.TenantManagement.TenantCreateDto.
export interface TenantCreateDto {
  name: string;
  adminEmailAddress: string;
  adminPassword: string;
  connectionStrings?: TenantConnectionStringCreateDto[];
}

// Volo.Abp.TenantManagement.TenantUpdateDto.
export interface TenantUpdateDto {
  name: string;
  concurrencyStamp?: string | null;
}

// ---------------------------------------------------------------------------
// Reset admin password (Finkit IFinkitTenantAppService)
// ---------------------------------------------------------------------------

// Finkit.TenantManagement.Dtos.ResetAdminPasswordDto.
export interface ResetAdminPasswordDto {
  oldPassword: string;
  newPassword: string;
}

export const MAX_NAME_LENGTH = 256;
export const MAX_ADMIN_EMAIL_LENGTH = 256;
export const MAX_PASSWORD_LENGTH = 128;
export const MIN_PASSWORD_LENGTH = 6;

// ---------------------------------------------------------------------------
// Edit tabs — Finkit.Needs.Tenants.Dtos.* (IFinkitTenantSetupService)
// Route base (ABP convention): /api/app/finkit-tenant-setup
// ---------------------------------------------------------------------------

// Finkit.Needs.Enums.Tenants.ContactDetailFor
export enum ContactDetailFor {
  PERSON1 = 1,
  PERSON2 = 2,
  BILLING = 3,
}

// Finkit.SharedKernel.Stores.Groups.TypeOfEntity (display names from [Display]).
export enum TypeOfEntity {
  NA = 0,
  InvestmentFirm = 1,
  CentralBank = 2,
  EEACreditInstitution = 3,
  BankInThirdCountry = 4,
  QualifyingMarketFund = 5,
  PaymentInstitution = 6,
  ElectronicMoneyInstitution = 7,
  ClearingHouse = 8,
  RecognizedExchange = 9,
  Other = 10,
}

// Finkit.Needs.Enums.Tenants.* — numeric values assumed source-order (0-based);
// confirm against the C# enums before relying on the wire values.
export enum BasisOfProvisionOfServices {
  FreedomToProvideServices = 0,
  FreedomOfEstablishment = 1,
}

export enum ProductType {
  DerivativesBased = 0,
  CashBased = 1,
}

export enum PermanentCapitalRequirement {
  Capital150K = 0,
  Capital750K = 1,
}

export enum PrudentialClassification {
  CreditInstitutionCRR = 0,
  IFClass1BCRR = 1,
  IFClass2DoAUnderwritingIFR = 2,
  IFClass2RAndTIFR = 3,
  IFClass3IFR = 4,
  AlternativeInvestmentFund = 5,
  UCITSAndItsManagement = 6,
  MiFIDPRUInvestmentFirm = 7,
}

export enum AccountingFramework {
  IFRS = 0,
  UKGaap = 1,
}

export enum LevelOfApplication {
  Solo = 0,
  Consolidated = 1,
}

export enum ReasonForFalling {
  NA = 0,
  UnionParentInvestmentFirm = 1,
  UnionParentInvestmentHoldingCompany = 2,
  UnionParentMixedFinancialHoldingCompany = 3,
}

export interface SelectListItem {
  value: string;
  text: string;
  isSelected?: boolean;
  disabled?: boolean;
}

// Finkit.Needs.Tenants.Dtos.Setup.TenantInformationCreateDto
export interface TenantInformationCreateDto {
  tenantId: string;
  typeOfEntity: TypeOfEntity;
  maxCoverageOfFunds: number;
  leiCode?: string | null;
  prudentialClassification: PrudentialClassification;
  permanentCapitalRequirement: PermanentCapitalRequirement;
  countryId: number;
  timeZone?: string | null;
  balanceHaircutPercentage: number;
  productType: ProductType;
  basisOfProvisionOfServices: BasisOfProvisionOfServices;
}

// Finkit.Needs.Tenants.Dtos.Setup.TenantSettingCreateDto
export interface TenantSettingCreateDto {
  tenantId: string;
  reportingCurrencyId: number;
  ccyCurrencyId: number;
  instrumentProfitabilityIncludeLp: boolean;
}

// Finkit.Needs.Tenants.Dtos.Edit.TenantClientInfoDto
export interface TenantClientInfoDto {
  tenantInformationCreateDto: TenantInformationCreateDto;
  tenantSettingCreateDto: TenantSettingCreateDto;
}

// Finkit.Needs.Tenants.Dtos.Edit.TenantReportInformationUpdateDto
export interface TenantReportInformationUpdateDto {
  tenantId?: string | null;
  trsIdentificationCodeOfEntity?: string | null;
  postalAddress?: string | null;
  faxNumber?: string | null;
  website?: string | null;
  address?: string | null;
  street?: string | null;
  building?: string | null;
  district?: string | null;
  city?: string | null;
  tin?: string | null;
  inType?: string | null;
  icfMemberName?: string | null;
  cifLicenseNumber?: string | null;
  monthlyAccountingStartingDate?: string | null;
  reportingEntityCode?: string | null;
  mifirStartingDate?: string | null;
  accountingFramework: AccountingFramework;
  levelOfApplication: LevelOfApplication;
  nameOfConsolidatingEntity?: string | null;
  reasonForFalling: ReasonForFalling;
  balanceSheetTotal?: number | null;
  combinedTotalAnnualGrossVenue?: number | null;
  totalAnnualGrossVenue?: number | null;
  totalExpensesOfProfits?: number | null;
  ownCashDeposit?: number | null;
}

// Finkit.Needs.Tenants.Dtos.Setup.TenantContactDetailCreateDto
export interface TenantContactDetailCreateDto {
  tenantId?: string | null;
  contactFor: ContactDetailFor;
  fullName: string;
  email: string;
  jobDescription: string;
  phone: string;
}

// Finkit.Needs.Tenants.Dtos.Dtos.Edit.TenantContactsDetailsDto
export interface TenantContactsDetailsDto {
  tenantContactDetailPerson1CreateDto: TenantContactDetailCreateDto;
  tenantContactDetailPerson2CreateDto?: TenantContactDetailCreateDto | null;
  tenantContactDetailBillingCreateDto?: TenantContactDetailCreateDto | null;
}

// Finkit.Needs.Tenants.Dtos.Setup.TenantIntegrationDatesCreateDto
export interface TenantIntegrationDatesCreateDto {
  tenantId: string;
  mainIntegrationDate: string;
  aMLSyncIntegrationDate?: string | null;
}

// Finkit.Needs.Tenants.Dtos.Setup.TenantSetupDto — the 3-step Setup wizard payload.
export interface TenantSetupDto {
  tenantInformationCreateDto: TenantInformationCreateDto;
  tenantSettingCreateDto: TenantSettingCreateDto;
  tenantIntegrationDatesCreateDto: TenantIntegrationDatesCreateDto;
  tenantReportsInformationCreateDto: TenantReportInformationUpdateDto;
  tenantContactDetailPerson1CreateDto: TenantContactDetailCreateDto;
  tenantContactDetailPerson2CreateDto?: TenantContactDetailCreateDto | null;
  tenantContactDetailBillingCreateDto?: TenantContactDetailCreateDto | null;
}

// Finkit.Needs.Tenants.Dtos.Edit.TenantRtExperienceDto
export interface TenantRtExperienceDto {
  refreshRatePriceMs: number;
  refreshRateTradeMs: number;
  refreshRateEquityMs: number;
  refreshRateLpMs: number;
  refreshRateNopMs: number;
  refreshRateMt5ApiDealsSec: number;
  refreshRateLpLiveClosedTradesAndDealsSec: number;
  refreshRateLpLiveOrdersSec: number;
  refreshRateAmlMoneyTransactionsSec: number;
  refreshRateAmlProfitThresholdSec: number;
  refreshRateRelatedClientsSec: number;
  refreshRateDealComparisonSec: number;
  refreshRateTraderSurveillanceSec: number;
  refreshRateFeedMonitoringSec: number;
  healthCheckDays: number;
  swapsPercentageDifference: number;
  amlMoneyTransactionsSync: boolean;
  amlProfitThresholdSync: boolean;
  dealComparisonSync: boolean;
  relatedClientsSync: boolean;
  mt5ApiDealsSync: boolean;
  lpLiveClosedTradesAndDealsSync: boolean;
  lpLiveOrdersSync: boolean;
  liveEquitySnapshotSync: boolean;
  liveOpenPositionsSnapshotSync: boolean;
  dailyEquitySnapshot: boolean;
  traderSurveillanceSync: boolean;
  dealSurveillanceSync: boolean;
  feedMonitoringSync: boolean;
}
